import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { LoginForm, LoginStep } from "@/components/pages/auth/login"
import {
	checkMfaRequired,
	enrollAndVerifyMfa,
	type MfaEnrollmentData,
	type MfaVerificationData,
	prepareMfaEnrollment,
	prepareMfaVerification,
	signInWithPassword,
	verifyMfa,
} from "@/lib/auth"
import { GENERIC_ERROR_MESSAGE, REQUIRE_MFA } from "@/lib/constants"
import { captureAuthError, getUserFriendlyError } from "@/lib/errors"
import type { LoginInput, MfaCodeInput } from "@/lib/schemas/auth"

export const Route = createFileRoute("/_auth/login")({
	component: LoginPage,
})

function LoginPage() {
	const navigate = useNavigate()
	const [step, setStep] = useState<LoginStep>(LoginStep.Credentials)
	const [email, setEmail] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [verificationData, setVerificationData] =
		useState<MfaVerificationData | null>(null)
	const [enrollmentData, setEnrollmentData] =
		useState<MfaEnrollmentData | null>(null)

	const runAuthAction = async (action: () => Promise<void>, email?: string) => {
		setError(null)
		setIsLoading(true)

		try {
			await action()
		} catch (err) {
			captureAuthError(err, email)
			setError(getUserFriendlyError(err))
		} finally {
			setIsLoading(false)
		}
	}

	const handleSubmitCredentials = async (values: LoginInput) => {
		setEmail(values.email)

		await runAuthAction(async () => {
			await signInWithPassword(values.email, values.password)

			if (!REQUIRE_MFA) {
				navigate({ to: "/" })
				return
			}

			const mfaCheck = await checkMfaRequired()

			if (mfaCheck.needsVerification && mfaCheck.factor) {
				setVerificationData(await prepareMfaVerification(mfaCheck.factor))
				setStep(LoginStep.MfaVerify)
				return
			}

			setEnrollmentData(await prepareMfaEnrollment())
			setStep(LoginStep.MfaEnroll)
		}, values.email)
	}

	const handleSubmitMfaVerify = async ({ totpCode }: MfaCodeInput) => {
		if (!verificationData) {
			setError(GENERIC_ERROR_MESSAGE)
			return
		}

		await runAuthAction(async () => {
			await verifyMfa(
				verificationData.factorId,
				verificationData.challengeId,
				totpCode,
			)
			navigate({ to: "/" })
		}, email ?? undefined)
	}

	const handleSubmitMfaEnroll = async ({ totpCode }: MfaCodeInput) => {
		if (!enrollmentData) {
			setError(GENERIC_ERROR_MESSAGE)
			return
		}

		await runAuthAction(async () => {
			await enrollAndVerifyMfa(enrollmentData.factorId, totpCode)
			navigate({ to: "/" })
		}, email ?? undefined)
	}

	const handleBack = () => {
		setStep(LoginStep.Credentials)
		setError(null)
		setVerificationData(null)
		setEnrollmentData(null)
	}

	return (
		<LoginForm
			step={step}
			isLoading={isLoading}
			error={error}
			enrollmentData={enrollmentData}
			onSubmitCredentials={handleSubmitCredentials}
			onSubmitMfaVerify={handleSubmitMfaVerify}
			onSubmitMfaEnroll={handleSubmitMfaEnroll}
			onBack={handleBack}
		/>
	)
}
